import axios, { AxiosError } from "axios";

const getBaseURL = (): string => {
  if (import.meta.env.PROD) {
    return `${window.location.origin}/api/v1`;
  }
  return import.meta.env.VITE_API_BASE_URL;
};

const axiosClient = axios.create({
  baseURL: getBaseURL(),
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || "30000"),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies for CORS requests
});

// Request interceptor (no manual token needed - cookies sent automatically)
axiosClient.interceptors.request.use(
  (config) => {
    // httpOnly cookies are sent automatically by browser
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle errors globally
axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    // Network/CORS/Client errors
    if (error.code === "ERR_NETWORK" || !error.response) {
      const enhancedError = new Error(
        "Network error. Please check your internet connection or CORS settings.",
      );
      (enhancedError as any).response = {
        data: {
          message: "Network error. Please check your internet connection.",
        },
      };
      return Promise.reject(enhancedError);
    }

    // Timeout errors
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      const enhancedError = new Error("Server unavailable. Request timeout.");
      (enhancedError as any).response = {
        data: { message: "Server unavailable. Request timeout." },
      };
      return Promise.reject(enhancedError);
    }

    // Server errors (5xx)
    if (error.response?.status && error.response.status >= 500) {
      const enhancedError = new Error(
        "Server unavailable. Please try again later.",
      );
      (enhancedError as any).response = {
        data: { message: "Server unavailable. Please try again later." },
      };
      return Promise.reject(enhancedError);
    }

    if (error.response?.status === 401) {
      // Skip refresh for /auth/me and /auth/refresh endpoints
      if (
        originalRequest?.url?.includes("/auth/me") ||
        originalRequest?.url?.includes("/auth/refresh")
      ) {
        return Promise.reject(error);
      }

      // Try to refresh token first (only once)
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          await axiosClient.post("/auth/refresh");
          // Retry original request
          return axiosClient.request(originalRequest);
        } catch {
          // Refresh failed - redirect to login
          window.location.href = "/login";
        }
      } else {
        // Already retried - redirect to login
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export default axiosClient;
